/**
 * The checkout's behavior half (design D7): reads the pick from the address
 * (`?service=&start=`, `?event=` or `?series=`), and once the visitor is signed in holds it
 * the way the app's service page does (open a flow, name the service and its
 * practitioner, hold the time, record it), or reserves the event's or the series' spot
 * (shared-client-screens D5; a series is drawn as a group event). Then
 * the address names the flow (`?flow=<token>`) and the summary reads the
 * server's flow. A flow already open for the same offering is resumed; one for
 * another offering is offered to finish or cancel. Draws nothing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LOCATION_KIND_LABEL, type SiteLocation, type SiteModality, type SitePhoto } from '@telify/shared';
import type { WebAppFlowContextResponse } from '@/api/types';
import { phoneCallStep } from '@/pages/booking/shared/bookingRules';

import { clientName, useWebSession } from '../../api/session';
import { useNextOpenings, useSiteEvent, useSiteSeries, useSiteService, useSiteStudio } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { amountText, formatDayLabel, formatDuration, formatLocation, getClientTimezone, slotTimeLabels, tzAbbrev } from '../../format';
import { activeFlowOf, isTimeGone, newIdempotencyKey, useWebFlowContext, useWebSavePhoneToCall, webFlow } from '../../hooks/useWebFlow';
import { usePageMover } from '../../pageMover';
import { bookingPhoto } from '../../ui/Photo';
import {
  contract,
  type CheckoutActions,
  type CheckoutEvents,
  type CheckoutOpeningDay,
  type CheckoutState,
  type CheckoutStep,
  type CheckoutSummary,
} from './contract';

export type CheckoutRuntime = PieceRuntime<typeof contract, CheckoutState, CheckoutActions>;

type Cursor = 'flow' | 'hub' | 'hold' | 'calendar';
type HoldPhase = 'idle' | 'holding' | 'taken' | 'failed' | 'other';

const TAKEN = 'That time was just taken. Please pick another.';
const NOT_STARTED = 'Could not start your booking. Please try again.';

/** What the summary is drawn from, before and after the hold. */
interface Offering {
  name: string;
  photo: SitePhoto | null;
  durationMinutes: number | null;
  startsAt: string | null;
  price: number | null;
  paymentRequirement: string;
  requiresApproval: boolean;
  modality: SiteModality | null;
  location: SiteLocation | null;
  path: string | null;
  provider: CheckoutSummary['provider'];
}

function dateLong(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: tz }).format(new Date(iso));
}

function locationText(loc: SiteLocation | null): string | null {
  if (!loc) return null;
  const { t1, t2 } = formatLocation(loc);
  if (loc.isVirtual) return t2 ? `${t1}, on ${t2}` : t1;
  return t2 ? `${t1}, ${t2}` : t1;
}

function totalNote(o: Offering): string | null {
  if (o.price == null || o.price <= 0) return null;
  if (o.requiresApproval) return 'Collected after approval';
  if (o.paymentRequirement === 'FULL_PAYMENT' || o.paymentRequirement === 'DEPOSIT') return 'Paid now by card';
  return 'Paid at the session';
}

function summaryOf(o: Offering, kind: 'service' | 'event', tz: string, fromServer: boolean): CheckoutSummary {
  const format = o.modality ? LOCATION_KIND_LABEL[o.modality] : null;
  const lead = kind === 'event' ? 'Group event' : o.durationMinutes ? formatDuration(o.durationMinutes) : null;
  return {
    name: o.name,
    photo: o.photo,
    sub: [lead, format].filter(Boolean).join(' · '),
    price: amountText(o.price),
    date: o.startsAt ? dateLong(o.startsAt, tz) : null,
    time: o.startsAt ? `${slotTimeLabels([o.startsAt], tz)[0]} ${tzAbbrev(tz, new Date(o.startsAt))}` : null,
    duration: kind === 'service' && o.durationMinutes ? formatDuration(o.durationMinutes) : null,
    location: locationText(o.location),
    online: Boolean(o.location?.isVirtual),
    byPhone: Boolean(o.location?.isByPhone),
    startsAt: o.startsAt,
    provider: o.provider,
    total: amountText(o.price),
    amount: o.price != null && o.price > 0 ? o.price : null,
    totalNote: totalNote(o),
    fromServer,
  };
}

/** The held flow's own offering, with the place and page address from the studio's catalog. */
function heldOffering(held: WebAppFlowContextResponse, site: { location: SiteLocation; modality: SiteModality; path: string } | undefined, eventStart: string | null): Offering | null {
  const service = held.context.service;
  if (!service) return null;
  return {
    name: service.name,
    photo: bookingPhoto(service),
    durationMinutes: service.duration,
    startsAt: held.flow.selectedSlot?.startsAt ?? held.flow.booking?.scheduledAt ?? eventStart,
    price: service.price,
    paymentRequirement: service.paymentRequirement,
    requiresApproval: service.requiresApproval,
    modality: site?.modality ?? null,
    location: site?.location ?? null,
    path: site?.path ?? null,
    provider: held.context.practitioner ? { name: held.context.practitioner.displayName, photoUrl: held.context.practitioner.profilePhoto } : null,
  };
}

function stepsFor(signedIn: boolean, held: WebAppFlowContextResponse | undefined, request: boolean): CheckoutStep[] {
  const last: Pick<CheckoutStep, 'id' | 'title'> = request ? { id: 'request', title: 'Your request' } : { id: 'payment', title: 'Payment' };
  if (!signedIn || !held) {
    return [
      { id: 'signIn', number: 1, title: 'Sign in', status: signedIn ? 'done' : 'active' },
      { ...last, number: 2, status: 'waiting' },
    ];
  }
  const done = held.flow.completedSteps;
  const formsApply =
    held.registrationFormId != null ||
    (held.context.service?.requiredForms ?? []).some((f) => f.isRequired) ||
    done.includes('REGISTRATION') ||
    done.includes('CUSTOM_FORMS');
  const formsOwed = held.flow.currentStep === 'REGISTRATION' || held.flow.currentStep === 'CUSTOM_FORMS';
  const steps: CheckoutStep[] = [{ id: 'signIn', number: 1, title: 'Sign in', status: 'done' }];
  if (formsApply) steps.push({ id: 'forms', number: 2, title: 'Forms', status: formsOwed ? 'active' : 'done' });
  steps.push({ ...last, number: steps.length + 1, status: formsOwed ? 'waiting' : 'active' });
  return steps;
}

function titleFor(signedIn: boolean, request: boolean, paymentRequirement: string): string {
  if (!signedIn) return request ? 'Sign in to send your request' : 'Sign in to confirm';
  if (request) return 'Review Your Request';
  return paymentRequirement === 'FULL_PAYMENT' || paymentRequirement === 'DEPOSIT' ? 'Complete payment' : 'Review Your Booking';
}

export function useCheckout(input: PieceInput<CheckoutEvents> = {}): CheckoutRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const [params, setParams] = useSearchParams();
  const flowToken = params.get('flow');
  const serviceId = flowToken ? null : params.get('service');
  const eventId = flowToken ? null : params.get('event');
  const seriesId = flowToken || eventId ? null : params.get('series');
  const start = params.get('start');
  const tz = useMemo(() => getClientTimezone(), []);

  const session = useWebSession();
  const signedIn = session.status === 'signedIn';
  const flow = useWebFlowContext(flowToken);
  const held = flow.data;
  // service-by-phone D5: the number to call, in the summary on a computer.
  const saveNumber = useWebSavePhoneToCall(flowToken);
  const [phoneChanging, setPhoneChanging] = useState(false);
  const callStep = phoneCallStep(held?.context.service?.phoneCall);

  // The offering in the studio's catalog: the pick's, or the held flow's (for its place and page).
  const heldEventId = held?.flow.selectedEventId ?? null;
  const heldSeriesId = held?.flow.selectedSeriesId ?? null;
  const catalogServiceId = held ? (heldEventId || heldSeriesId ? undefined : held.context.service?.id) : (serviceId ?? undefined);
  const catalogEventId = held ? (heldSeriesId ? undefined : (heldEventId ?? undefined)) : (eventId ?? undefined);
  const catalogSeriesId = held ? (heldSeriesId ?? undefined) : (seriesId ?? undefined);
  const siteService = useSiteService(catalogServiceId);
  const siteEvent = useSiteEvent(catalogEventId);
  const siteSeries = useSiteSeries(catalogSeriesId);
  const kind: 'service' | 'event' = held ? (heldEventId || heldSeriesId ? 'event' : 'service') : eventId || seriesId ? 'event' : 'service';

  // ── The hold (the app's commit machine, on the website session) ──
  const [phase, setPhase] = useState<HoldPhase>('idle');
  const [problem, setProblem] = useState<string | null>(null);
  const [other, setOther] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const machine = useRef<{ cursor: Cursor; flowToken: string | null; key: string | null; running: boolean }>({
    cursor: 'flow',
    flowToken: null,
    key: null,
    running: false,
  });
  const otherFlow = useWebFlowContext(other);

  const runHold = useCallback(async () => {
    const m = machine.current;
    if (m.running) return;
    m.running = true;
    setPhase('holding');
    setProblem(null);
    try {
      if (eventId) {
        m.key ??= newIdempotencyKey();
        try {
          m.flowToken = (await webFlow.createEventFlow(eventId, m.key)).flowToken;
        } catch (e) {
          const open = activeFlowOf(e);
          if (open?.selectedEventId !== eventId) throw e;
          m.flowToken = open.flowToken;
        }
      } else if (seriesId) {
        m.key ??= newIdempotencyKey();
        try {
          m.flowToken = (await webFlow.createSeriesFlow(seriesId, m.key)).flowToken;
        } catch (e) {
          const open = activeFlowOf(e);
          if (open?.selectedSeriesId !== seriesId) throw e;
          m.flowToken = open.flowToken;
        }
      } else if (serviceId && start) {
        if (m.cursor === 'flow') {
          try {
            m.flowToken = (await webFlow.createServiceFlow(serviceId)).flowToken;
            m.cursor = 'hub';
          } catch (e) {
            const open = activeFlowOf(e);
            if (open?.selectedServiceId !== serviceId) throw e;
            // The same service is already open on this client: carry on with it. Past its time (its forms, payment
            // or review) the client came back to pick another, so, as the app's Back does, it steps back to its
            // time and holds the new one (Gabriel's 2026-11-02 walk).
            const resumeStep = (e as { body?: { resumeStep?: unknown } }).body?.resumeStep;
            if (resumeStep !== 'HUB' && resumeStep !== 'CALENDAR' && !(await webFlow.backToTime(open.flowToken))) throw e;
            m.flowToken = open.flowToken;
            m.cursor = resumeStep === 'HUB' ? 'hub' : 'hold';
          }
        }
        if (m.cursor === 'hub') {
          const practitionerId = await webFlow.servicePractitioner(serviceId);
          await webFlow.completeHub(m.flowToken!, serviceId, practitionerId);
          m.cursor = 'hold';
        }
        if (m.cursor === 'hold') {
          m.key ??= newIdempotencyKey();
          await webFlow.hold(m.flowToken!, start, m.key);
          m.cursor = 'calendar';
        }
        await webFlow.completeCalendar(m.flowToken!, start);
      } else {
        return;
      }
      const token = m.flowToken!;
      machine.current = { cursor: 'flow', flowToken: null, key: null, running: false };
      setPhase('idle');
      emit('held', { flowToken: token });
      navigate(`/checkout?${new URLSearchParams({ flow: token })}`, { replace: true });
    } catch (e) {
      const open = activeFlowOf(e);
      if (open) {
        setOther(open.flowToken);
        setPhase('other');
      } else if (serviceId && start && isTimeGone(e)) {
        // The flow and its service stay; the next pick holds a new time with a new key.
        m.cursor = 'hold';
        m.key = null;
        setProblem(TAKEN);
        setPhase('taken');
        emit('timeTaken', { startsAt: start });
      } else {
        setProblem(e instanceof Error && e.message ? e.message : NOT_STARTED);
        setPhase('failed');
      }
    } finally {
      m.running = false;
    }
  }, [eventId, seriesId, serviceId, start, emit, navigate]);

  const pickReady = Boolean(eventId || seriesId || (serviceId && start));
  useEffect(() => {
    if (signedIn && !flowToken && pickReady && phase === 'idle') void runHold();
  }, [signedIn, flowToken, pickReady, phase, runHold]);

  // ── The next openings, while the chosen time is taken ──
  const openingsQuery = useNextOpenings(phase === 'taken' ? (serviceId ?? undefined) : undefined, tz);
  const openings = useMemo<CheckoutOpeningDay[]>(() => {
    if (phase !== 'taken') return [];
    return (openingsQuery.data?.days ?? [])
      .filter((d) => d.times.length > 0)
      .map((d) => {
        const labels = slotTimeLabels(d.times.map((t) => t.startsAt), tz);
        return {
          date: d.date,
          label: formatDayLabel(d.times[0]!.startsAt, tz),
          times: d.times.map((t, i) => ({ startsAt: t.startsAt, label: labels[i]! })),
        };
      });
  }, [phase, openingsQuery.data, tz]);

  const actions = useMemo<CheckoutActions>(
    () => ({
      back: () => navigate(backHref.current ?? '/'),
      pickOpening: (startsAt) => {
        if (!serviceId) return;
        setParams({ service: serviceId, start: startsAt }, { replace: true });
        setProblem(null);
        setPhase('idle');
      },
      continueOther: () => {
        if (!other) return;
        const token = other;
        setOther(null);
        machine.current = { cursor: 'flow', flowToken: null, key: null, running: false };
        setPhase('idle');
        navigate(`/checkout?${new URLSearchParams({ flow: token })}`);
      },
      startOver: () => {
        if (!other || cancelling) return;
        setCancelling(true);
        webFlow
          .cancel(other)
          .then(() => {
            setOther(null);
            machine.current = { cursor: 'flow', flowToken: null, key: null, running: false };
            setPhase('idle');
          })
          .catch(() => setProblem("Couldn't cancel your other booking. Please try again."))
          .finally(() => setCancelling(false));
      },
      changePhone: () => setPhoneChanging(true),
      savePhone: async (phone) => {
        await saveNumber(phone);
        setPhoneChanging(false);
      },
      cancelPhone: () => setPhoneChanging(false),
    }),
    [navigate, serviceId, setParams, other, cancelling, saveNumber],
  );

  // ── What is drawn ──
  // Before the hold the session is with the studio's guide; the held flow names its own practitioner.
  const studioGuide = useSiteStudio().data?.guide;
  const guide = useMemo(() => (studioGuide ? { name: studioGuide.name, photoUrl: studioGuide.photoUrl } : null), [studioGuide]);
  const pick: Offering | null = useMemo(() => {
    if (held) {
      const site = heldSeriesId ? siteSeries.data : heldEventId ? siteEvent.data : siteService.data;
      return heldOffering(held, site, siteEvent.data?.startTime ?? null);
    }
    if (seriesId && siteSeries.data) {
      const se = siteSeries.data;
      return {
        name: se.title,
        photo: se.photo,
        durationMinutes: null,
        startsAt: se.dates[0] ?? null,
        price: se.price.kind === 'FREE' ? null : se.price.amount,
        paymentRequirement: se.paymentRequirement,
        requiresApproval: false,
        modality: se.modality,
        location: se.location,
        path: se.path,
        provider: guide,
      };
    }
    if (eventId && siteEvent.data) {
      const ev = siteEvent.data;
      return {
        name: ev.title,
        photo: ev.photo,
        durationMinutes: null,
        startsAt: ev.startTime,
        price: ev.price.kind === 'FREE' ? null : ev.price.amount,
        paymentRequirement: ev.payment.paymentRequirement,
        requiresApproval: false,
        modality: ev.modality,
        location: ev.location,
        path: ev.path,
        provider: guide,
      };
    }
    if (serviceId && siteService.data) {
      const s = siteService.data;
      return {
        name: s.name,
        photo: s.photo,
        durationMinutes: s.durationMinutes,
        startsAt: start,
        price: s.price.kind === 'FREE' ? null : s.price.amount,
        paymentRequirement: s.paymentRequirement,
        requiresApproval: s.requiresApproval,
        modality: s.modality,
        location: s.location,
        path: s.path,
        provider: guide,
      };
    }
    return null;
  }, [held, heldEventId, heldSeriesId, siteEvent.data, siteService.data, siteSeries.data, eventId, seriesId, serviceId, start, guide]);

  // Between the hold and the flow's first read the address names only the flow: the pick stays drawn.
  const lastPick = useRef<Offering | null>(null);
  if (pick) lastPick.current = pick;
  const shown = pick ?? (flowToken && !held ? lastPick.current : null);

  const backHref = useRef<string | null>(null);
  backHref.current = shown?.path ?? null;

  const request = shown?.requiresApproval ?? false;
  const nothingNamed = !flowToken && !serviceId && !eventId && !seriesId;
  const notFound =
    nothingNamed ||
    (Boolean(serviceId) && !start) ||
    flow.isError ||
    (!held && (siteService.isError || siteEvent.isError || siteSeries.isError));
  const status: CheckoutState['status'] = notFound ? 'not-found' : !shown && (session.status === 'loading' || !flowToken || signedIn) ? 'loading' : 'ready';

  const hold: CheckoutState['hold'] = !signedIn
    ? 'guest'
    : held
      ? 'held'
      : phase === 'taken' || phase === 'failed' || phase === 'other'
        ? phase
        : 'holding';

  const otherData = otherFlow.data;
  return {
    state: {
      status,
      session: session.status,
      client: signedIn && session.user ? clientName(session.user) : null,
      kind,
      request,
      title: titleFor(signedIn, request, shown?.paymentRequirement ?? 'NONE'),
      back: shown?.path ? { label: `Back to ${shown.name}`, href: shown.path } : null,
      steps: stepsFor(signedIn, held, request),
      hold,
      holdNote: signedIn ? null : kind === 'event' ? 'Your spot is held once you sign in.' : 'The time is held once you sign in.',
      problem: hold === 'taken' || hold === 'failed' || hold === 'other' ? problem : null,
      openings,
      summary: shown ? summaryOf(shown, kind, tz, Boolean(held)) : null,
      phoneCall: callStep && {
        line: callStep.line,
        phone: callStep.phone,
        needsNumber: callStep.needsNumber,
        changing: phoneChanging,
        asking: callStep.needsNumber || phoneChanging,
      },
      otherBooking:
        hold === 'other'
          ? {
              name: otherData?.context.service?.name ?? null,
              whenIso: otherData?.flow.selectedSlot?.startsAt ?? otherData?.flow.booking?.scheduledAt ?? null,
              imageUrl: otherData?.context.service?.imageUrl ?? null,
              focalPointX: otherData?.context.service?.focalPointX ?? null,
              focalPointY: otherData?.context.service?.focalPointY ?? null,
              focalPointScale: otherData?.context.service?.focalPointScale ?? null,
              cancelling,
            }
          : null,
    },
    actions,
    options,
  };
}
